import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, PhoneCall, ArrowRight, X, Building2, Clock, StickyNote } from "lucide-react";
import api from "@/lib/api";
import { isInvalidPhone } from "@/lib/crm";

const POLL_MS = 30000;
const STORE_KEY = "aadv_reminders_dismissed";

function loadDismissed() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) || "{}");
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    const clean = {};
    for (const [k, v] of Object.entries(raw)) if (v > cutoff) clean[k] = v;
    localStorage.setItem(STORE_KEY, JSON.stringify(clean));
    return clean;
  } catch {
    return {};
  }
}

function persistDismissed(map) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString("it-IT", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Rome",
    });
  } catch {
    return "";
  }
}

// --- Audio: breve suono di notifica (se consentito dal browser) ---
let audioCtx = null;
function ensureAudioCtx() {
  if (!audioCtx) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) audioCtx = new AC();
    } catch {
      audioCtx = null;
    }
  }
  if (audioCtx && audioCtx.state === "suspended") {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}

function playBeep() {
  // Best-effort: se il browser blocca l'audio (nessun gesto utente) il popup resta comunque visibile.
  try {
    const ctx = ensureAudioCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    [880, 1175].forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.value = freq;
      const t0 = now + i * 0.22;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.18, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.2);
      o.start(t0);
      o.stop(t0 + 0.22);
    });
  } catch {
    /* audio non disponibile: ignora */
  }
}

// --- Indicatore visivo aggiuntivo: lampeggio del titolo della scheda browser ---
let titleFlashTimer = null;
function flashTitle(text) {
  try {
    if (titleFlashTimer) {
      clearInterval(titleFlashTimer);
      titleFlashTimer = null;
    }
    const base = document.title.replace(/^🔔\s*/, "");
    let on = false;
    let count = 0;
    titleFlashTimer = setInterval(() => {
      document.title = on ? base : `🔔 ${text}`;
      on = !on;
      count += 1;
      if (count >= 12) {
        clearInterval(titleFlashTimer);
        titleFlashTimer = null;
        document.title = base;
      }
    }, 800);
  } catch {
    /* ignore */
  }
}

function notifyAlert(title) {
  playBeep();
  flashTitle(title);
}

export default function AppointmentReminders() {
  const navigate = useNavigate();
  const shownRef = useRef(new Set());
  const dismissedRef = useRef(loadDismissed());

  useEffect(() => {
    let active = true;

    // Sblocca l'audio al primo gesto utente (policy autoplay dei browser)
    const unlock = () => ensureAudioCtx();
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);

    const dismiss = (key, id) => {
      dismissedRef.current[key] = Date.now();
      persistDismissed(dismissedRef.current);
      toast.dismiss(id);
    };

    const showAppt = (appt, key, mins) => {
      const lead = appt.lead || {};
      const name = [lead.first_name, lead.last_name].filter(Boolean).join(" ") || "Lead";
      const time = fmtTime(appt.start_at);
      const whenLabel = mins >= 1 ? `tra ${mins} ${mins === 1 ? "minuto" : "minuti"}` : "a breve";
      notifyAlert(`Appuntamento: ${name}`);
      toast.custom(
        (id) => (
          <div
            data-testid="appt-reminder-toast"
            className="w-[340px] rounded-lg border border-primary/40 bg-card text-card-foreground shadow-xl p-4 relative animate-fade-up ring-2 ring-primary/20"
          >
            <button
              data-testid="appt-reminder-dismiss"
              onClick={() => dismiss(key, id)}
              className="absolute top-2.5 right-2.5 p-1 rounded-md text-muted-foreground hover:bg-accent transition-colors"
              title="Chiudi"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 text-primary font-heading font-bold text-sm pr-6">
              <span className="relative inline-flex">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary/40 animate-ping" />
                <Phone className="w-4 h-4 relative" />
              </span>
              Appuntamento {whenLabel}
            </div>
            <div className="mt-2">
              <div className="font-semibold text-sm">Chiama {name} alle {time}</div>
              {lead.company && (
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {lead.company}
                </div>
              )}
              <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {time}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              {lead.phone && !isInvalidPhone(lead.phone) && (
                <a
                  data-testid="appt-reminder-call"
                  href={`tel:${lead.phone}`}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white font-medium text-sm transition-colors"
                >
                  <Phone className="w-4 h-4" /> Chiama
                </a>
              )}
              <button
                data-testid="appt-reminder-view"
                onClick={() => {
                  dismiss(key, id);
                  if (lead.id) navigate(`/leads/${lead.id}`);
                }}
                className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-md border font-medium text-sm hover:bg-accent transition-colors"
              >
                Vedi lead <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ),
        { id: key, duration: Infinity }
      );
    };

    const showTask = (task, key, mins) => {
      const lead = task.lead || {};
      const name = [lead.first_name, lead.last_name].filter(Boolean).join(" ") || "Lead";
      const time = fmtTime(task.due_at);
      const note = task.note || task.title || "";
      const whenLabel = mins >= 1 ? `tra ${mins} ${mins === 1 ? "minuto" : "minuti"}` : "a breve";
      const label = lead.status === "Mandare mail" || /mail/i.test(task.title || "") ? "Mandare mail" : "Richiamo";
      notifyAlert(`${label}: ${name}`);
      toast.custom(
        (id) => (
          <div
            data-testid="call-reminder-toast"
            className="w-[340px] rounded-lg border border-primary/40 bg-card text-card-foreground shadow-xl p-4 relative animate-fade-up ring-2 ring-primary/20"
          >
            <button
              data-testid="call-reminder-dismiss"
              onClick={() => dismiss(key, id)}
              className="absolute top-2.5 right-2.5 p-1 rounded-md text-muted-foreground hover:bg-accent transition-colors"
              title="Chiudi"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 text-primary font-heading font-bold text-sm pr-6">
              <span className="relative inline-flex">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary/40 animate-ping" />
                <PhoneCall className="w-4 h-4 relative" />
              </span>
              <span data-testid="call-reminder-label">{label} {whenLabel}</span>
            </div>
            <div className="mt-2 space-y-0.5">
              <div className="font-semibold text-sm">{name}</div>
              {lead.phone && !isInvalidPhone(lead.phone) && (
                <a
                  data-testid="call-reminder-phone"
                  href={`tel:${lead.phone}`}
                  className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground hover:underline transition-colors"
                >
                  <Phone className="w-3 h-3" />
                  {lead.phone}
                </a>
              )}
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Ore {time}
              </div>
              {note && (
                <div className="text-xs text-muted-foreground flex items-start gap-1 pt-0.5">
                  <StickyNote className="w-3 h-3 mt-0.5 shrink-0" />
                  <span className="break-words">{note}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <button
                data-testid="call-reminder-open"
                onClick={() => {
                  dismiss(key, id);
                  if (lead.id) navigate(`/leads/${lead.id}`);
                }}
                className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 font-medium text-sm transition-opacity"
              >
                Apri lead <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="call-reminder-close"
                onClick={() => dismiss(key, id)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-md border font-medium text-sm hover:bg-accent transition-colors"
              >
                Chiudi
              </button>
            </div>
          </div>
        ),
        { id: key, duration: Infinity }
      );
    };

    const poll = async () => {
      try {
        const [apptRes, taskRes] = await Promise.all([
          api.get("/appointments/reminders").catch(() => ({ data: [] })),
          api.get("/tasks/reminders").catch(() => ({ data: [] })),
        ]);
        if (!active) return;
        const now = Date.now();
        for (const appt of apptRes.data || []) {
          const start = new Date(appt.start_at).getTime();
          if (Number.isNaN(start)) continue;
          const mins = (start - now) / 60000;
          if (mins > 5.2 || mins < -3) continue;
          const key = `appt:${appt.id}:${appt.start_at}`;
          if (dismissedRef.current[key] || shownRef.current.has(key)) continue;
          shownRef.current.add(key);
          showAppt(appt, key, Math.max(0, Math.round(mins)));
        }
        for (const task of taskRes.data || []) {
          const due = new Date(task.due_at).getTime();
          if (Number.isNaN(due)) continue;
          const mins = (due - now) / 60000;
          if (mins > 5.2 || mins < -3) continue;
          const key = `task:${task.id}:${task.due_at}`;
          if (dismissedRef.current[key] || shownRef.current.has(key)) continue;
          shownRef.current.add(key);
          showTask(task, key, Math.max(0, Math.round(mins)));
        }
      } catch {
        /* silent: reminders are best-effort */
      }
    };

    poll();
    const iv = setInterval(poll, POLL_MS);
    return () => {
      active = false;
      clearInterval(iv);
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [navigate]);

  return null;
}
